import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { Case, Default, Else, If, Switch, Then, When } from 'react-if'
import { InternalLink } from '@/atoms/InternalLink'
import { ClosedCaptionsBadge, ContentRatingBadge, HighDefinitionBadge } from '@/atoms/MetadataBadge'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsH1, AsH2, AsH4, CaptionMD, HeadingLG, HeadingXS, LabelSM, ParagraphLG } from '@/atoms/Text'
import { projectTypes } from '@/constants/projectTypes'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { LoginToWatchButtonGroup } from '@/molecules/LoginToWatchButtonGroup'
import { PayItForwardButton } from '@/molecules/PayItForwardButton'
import { ComingSoonButton, ContentRestrictedButton, GeoBlockedButton } from '@/molecules/UnavailableButtons'
import { Episode, GetEpisodeResponse } from '@/services/ProjectsService'
import { useUser } from '@/services/UserService'
import { isEarlyAccessEpisode } from '@/utils/GuildUtil/GuildUtil'
import { useLoginUrl } from '@/utils/auth-utils'
import { useTranslate, Translate } from '@/utils/translate/translate-client'

interface WatchEpisodeHeroProps {
  loading?: boolean
  data?: GetEpisodeResponse
  episode: Episode
  hasPayItForward?: boolean
  slug: string
  isGeoBlocked?: boolean
}

export const WatchEpisodeHero: React.FC<WatchEpisodeHeroProps> = ({
  loading,
  data,
  episode,
  hasPayItForward,
  slug,
  isGeoBlocked = false,
}) => {
  const { t } = useTranslate('watch')
  const [expanded, setExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const { metadata, projectType, releaseDate } = useProjectMeta()
  const { loginUrl, trackLoginStarted } = useLoginUrl()
  const { isLoggedIn } = useUser()
  const releaseYear = releaseDate ? DateTime.fromISO(releaseDate).year : null

  const handleReadMoreOrLess = () => {
    setExpanded(!expanded)
  }

  useEffect(() => {
    const handleResize = () => {
      if (!loading && data) {
        const element = document.getElementById('show-description')

        if (element) {
          setIsTruncated(element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth)
        }
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [loading, data])

  const projectSlug = data?.project?.slug

  return (
    <div className="relative w-full overflow-hidden md:flex">
      <div className="bg-core-gray-950 px-4 sm:px-8 md:px-12 xl:px-16">
        {(data?.episode?.posterLandscapeCloudinaryPath || data?.episode?.posterCloudinaryPath) && (
          <div className="fixed left-0 top-0 min-h-[70vh] w-screen transition-opacity">
            <ResponsiveBackgroundImage
              preBackgroundStyles="linear-gradient(0deg, #151515 0%, rgba(21, 21, 21, 0) 50%), linear-gradient(225deg, rgba(21, 21, 21, 0.2) 50%, #151515 99.89%),"
              src={data.episode.posterLandscapeCloudinaryPath ?? data.episode.posterCloudinaryPath}
              className={'bg-top'}
            />
            <div className="absolute inset-x-0 top-0 h-12 w-full bg-gradient-to-b from-gray-950 to-transparent sm:h-24 lg:h-32" />
          </div>
        )}
        <div className="relative my-40 w-full md:max-w-[650px]">
          <header>
            {data?.project?.name && (
              <HeadingLG as={AsH1} className="mb-4 text-center md:text-left" weight="bold">
                {data.project.name}
              </HeadingLG>
            )}

            <When condition={episode?.name && projectType !== 'movie'}>
              <HeadingXS as={AsH2} className="mb-2 text-center md:text-left" weight="bold">
                {episode.name}
              </HeadingXS>
            </When>
            <When condition={episode.subtitle && episode.subtitle !== data?.project?.name}>
              <HeadingXS as={AsH4} className="mb-2 text-center text-2xl md:text-left" weight="semibold">
                {episode.subtitle}
              </HeadingXS>
            </When>
          </header>
          <div className="mb-8 flex flex-col items-center md:items-start">
            <ParagraphLG
              id="show-description"
              className={classNames('text-center md:text-left transition-[max-height] duration-[600ms] ease-in-out', {
                'max-h-[60px] line-clamp-2': !expanded,
                'overflow-hidden max-h-96': expanded,
              })}
              color="gray-100"
            >
              {data?.episode?.description}
            </ParagraphLG>
            <When condition={isTruncated}>
              <button onClick={handleReadMoreOrLess}>
                <ParagraphLG className="cursor-pointer underline" color="oxide">
                  <If condition={expanded}>
                    <Then>{t('readLess', 'read less')}</Then>
                    <Else>{t('readMore', 'read more')}</Else>
                  </If>
                </ParagraphLG>
              </button>
            </When>
          </div>
          <div className="mb-9 flex flex-row flex-wrap justify-center gap-2 text-gray-500 md:justify-start md:text-gray-300">
            {releaseYear && (
              <>
                <CaptionMD>{releaseYear}</CaptionMD>
                <CaptionMD>•</CaptionMD>
              </>
            )}
            <CaptionMD className="whitespace-nowrap">
              <Switch>
                <Case condition={projectType === projectTypes.series}>
                  <Translate i18nKey="tvSeries" t={t}>
                    TV Series
                  </Translate>
                </Case>
                <Case condition={projectType === projectTypes.movie}>
                  <Translate i18nKey="movie" t={t}>
                    Movie
                  </Translate>
                </Case>
                <Case condition={projectType === projectTypes.podcast}>
                  <Translate i18nKey="podcast" t={t}>
                    Podcast
                  </Translate>
                </Case>
                <Case condition={projectType === projectTypes.special}>
                  <Translate i18nKey="special" t={t}>
                    Special
                  </Translate>
                </Case>
              </Switch>
            </CaptionMD>
            {metadata?.genres?.length && metadata.genres.length > 0 && <CaptionMD>•</CaptionMD>}
            {metadata?.genres?.map((genre: string, index) => {
              return (
                <div className="flex gap-2" key={genre}>
                  <CaptionMD>{genre}</CaptionMD>
                  <When condition={index < metadata.genres.length - 1}>
                    <CaptionMD>•</CaptionMD>
                  </When>
                </div>
              )
            })}
            <div className="flex flex-row gap-2 md:basis-full">
              <ContentRatingBadge contentRating={metadata?.contentRating} slug={slug} />
              <HighDefinitionBadge slug={slug} />
              <ClosedCaptionsBadge />
            </div>
          </div>
          <div className="mt-20 flex flex-col md:flex-row">
            <Switch>
              <Case condition={isGeoBlocked}>
                <GeoBlockedButton className="w-full px-6 py-3 md:w-[220px]" />
              </Case>
              <Case condition={data?.episode?.unavailableReason === 'LIMITED_PROFILE'}>
                <ContentRestrictedButton />
              </Case>
              <Case
                condition={
                  data?.episode?.earlyAccessDate &&
                  data?.episode?.publiclyAvailableDate &&
                  data.episode.earlyAccessDate === data.episode.publiclyAvailableDate
                }
              >
                <ComingSoonButton />
              </Case>
              <Default>
                <LoginToWatchButtonGroup
                  episode={episode}
                  isGuildMemberContent={isEarlyAccessEpisode(episode)}
                  slug={episode.projectSlug}
                  trailer={data?.project?.trailers?.[0]}
                  project={data?.project}
                />
              </Default>
            </Switch>
            {hasPayItForward && projectSlug && (
              <PayItForwardButton
                projectSlug={projectSlug}
                pifEnabled={true}
                utmSource="watch-episode-hero"
                className={
                  'mt-4 box-border h-8 min-h-[50px] w-full rounded-md backdrop-blur-sm md:ml-4 md:mt-0 md:w-fit'
                }
              />
            )}
          </div>
          <When condition={!isLoggedIn}>
            <LabelSM className="mt-4 text-center md:text-left">
              <Translate t={t} i18nKey="logInIfGuildMember">
                Already a member of the Angel Guild?&nbsp;&nbsp;&nbsp;
                <InternalLink
                  className="text-oxide-bright underline"
                  href={loginUrl}
                  onClick={() => trackLoginStarted('WatchEpisodeHero')}
                >
                  Log In
                </InternalLink>
              </Translate>
            </LabelSM>
          </When>
        </div>
      </div>
    </div>
  )
}
